import axios from 'axios';
import { axiosNoAuthClient } from '@/shared/apis/config';
import {
  createTokenCookie,
  deleteCookie,
  getCookie,
} from '@/shared/server-actions';
import { useAuthStore } from '@/shared/store';
import { ApiResponse } from '@/shared/types/api/response';
import pMemoize from 'p-memoize';
import ExpiryMap from 'expiry-map';
import { useTokenStore } from '@/shared/store/token-store';

const P_MEMOIZE_CACHE = new ExpiryMap(2000);

const AUTH_BROADCAST_CHANNEL = 'auth-token-refresh';

let broadcastChannel: BroadcastChannel | null = null;

function getAuthBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (broadcastChannel) return broadcastChannel;

  try {
    broadcastChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    broadcastChannel.onmessage = (event) => {
      const { type, accessToken } = event.data ?? {};
      if (type === 'TOKEN_REFRESHED' && accessToken) {
        useTokenStore.getState().setAccessToken(accessToken);
      }
    };
  } catch {
    // BroadcastChannel not supported (e.g. some WebView environments)
  }

  return broadcastChannel;
}

if (typeof window !== 'undefined') {
  getAuthBroadcastChannel();
}

const TOKEN_INVALID_CODES = [1002, 1003, 1004, 1005];
/**
 * access 토큰 만료 시간을 반환합니다.
 * @returns 토큰 만료 시간 (ms)
 */
export const getAccessTokenExpiry = () => {
  return 60 * 60; // 1시간
};

/**
 * refresh 토큰 만료 시간을 반환합니다.
 * @param isRememberMe - 리마인더 미 체크 여부
 * @returns 토큰 만료 시간 (ms)
 */
export const getRefreshTokenExpiry = (
  isRememberMe: boolean,
): number | undefined => {
  return isRememberMe ? 60 * 60 * 24 * 30 : 60 * 60 * 24 * 14; // 30일 동안 유효 또는 14일
};

export interface RefreshAccessTokenRes {
  accessToken: string;
  refreshToken: string;
}

export const refreshAccessTokenApi = async (refreshToken: string) => {
  try {
    const response = await axiosNoAuthClient.post<
      ApiResponse<RefreshAccessTokenRes>
    >('/v1.0/sign/token-refresh', {
      refreshToken,
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const tokenRefresh = pMemoize(
  async (): Promise<string | void> => {
    const currentRefreshToken = await getCookie('r_t');
    const isRememberMe = useAuthStore.getState().isRememberMe;

    try {
      if (!currentRefreshToken) {
        const { SessionExpiredError } =
          await import('@/shared/apis/config/interceptors/response/token-refresh-handler');
        throw new SessionExpiredError();
      }
      const { data } = await refreshAccessTokenApi(currentRefreshToken);
      const { accessToken, refreshToken } = data;
      useTokenStore.getState().setAccessToken(accessToken);

      getAuthBroadcastChannel()?.postMessage({
        type: 'TOKEN_REFRESHED',
        accessToken,
      });

      try {
        await createTokenCookie({
          name: 'r_t',
          value: refreshToken,
          maxAge: getRefreshTokenExpiry(isRememberMe),
        });
      } catch (cookieError) {
        console.error(
          '[tokenRefresh] createTokenCookie failed, accessToken is still valid in memory:',
          cookieError,
        );
      }

      return accessToken;
    } catch (error) {
      // refreshAccessTokenApi 실패만 여기서 처리
      if (axios.isAxiosError(error) && error.response) {
        const errorCode = Number(error.response.data?.code);
        if (errorCode && TOKEN_INVALID_CODES.includes(errorCode)) {
          await deleteCookie('r_t');
        }
      }

      return Promise.reject(error);
    }
  },
  {
    cache: P_MEMOIZE_CACHE,
  },
);
